import {Controller} from "stimulus";
import * as Turbo from "@hotwired/turbo";

export default class AdminModalController extends Controller {
  static values = {
    redirect: String,
    open: {type: Boolean, default: true},
    remove: {type: Boolean, default: true},
    confirmBeforeClose: {type: Boolean, default: false},
    confirmBeforeCloseMessage: String,
    queryParam: String,
    queryParamValue: {type: String, default: "true"},
  };

  declare readonly hasRedirectValue: boolean;
  declare readonly hasQueryParamValue: boolean;
  declare openValue: boolean;
  declare redirectValue: string;
  declare removeValue: boolean;
  declare confirmBeforeCloseValue: boolean;
  declare confirmBeforeCloseMessageValue: string;
  declare queryParamValue: string;
  declare queryParamValueValue: string;

  connect() {
    if (this.openValue) {
      this.element.ariaModal = "true";
      this.dispatch("open", {prefix: "tlw-modal", bubbles: true});
    } else {
      document.addEventListener("tlw-modal:trigger-open", this.handleOpen);
    }

    if (this.hasQueryParamValue) this.addUrlQueryParam();

    document.addEventListener("keyup", this.keyup);
    document.addEventListener("tlw-modal:trigger-close", this.handleClose);
    this.fixAutoFocusAttribute();
  }

  disconnect() {
    if (this.openValue) document.documentElement.dispatchEvent(new CustomEvent("tlw-modal:close", {bubbles: true}));

    document.removeEventListener("keyup", this.keyup);
    document.removeEventListener("tlw-modal:trigger-open", this.handleOpen);
    document.removeEventListener("tlw-modal:trigger-close", this.handleClose);
  }

  // Inputs with "autofocus" will only do so *once* via turbo streams
  // For subsequent modal opens, turbo will not mutate the DOM sufficiently for the browser to focus it again automatically.
  fixAutoFocusAttribute() {
    const elementToFocus = this.element.querySelector<HTMLInputElement>("input[autofocus=\"autofocus\"]");
    if (elementToFocus) {
      elementToFocus.focus();
    }
  }

  handleClose = () => {
    // Only close the topmost modal when multiple are open
    if (!this.isActiveModal()) return;

    if (!this.confirmBeforeCloseValue) {
      this.close();
      return;
    }
    if (window.confirm(this.confirmBeforeCloseMessageValue)) {
      this.close();
    }
  };

  close() {
    this.element.removeAttribute("aria-modal");

    const event = this.dispatch("close", {prefix: "tlw-modal", bubbles: true, cancelable: true});
    if (event.defaultPrevented) return;

    if (this.hasRedirectValue) {
      const currentPath = window.location.href.split(window.location.host)[1];

      if (currentPath != this.redirectValue) {
        Turbo.visit(this.redirectValue);
      }
    }

    if (this.hasQueryParamValue) this.removeUrlQueryParam();

    const finalCloseAction = () => this.removeValue
      ? this.element.remove()
      : this.element.classList.add("hidden");

    // Animate out if inner dialog has data-exit-styles
    const dialog = this.element.querySelector<HTMLElement>("[role=dialog]");
    const dialogExitStyles = dialog?.dataset?.exitStyles?.split(" ");
    if (dialog && dialogExitStyles) {
      this.element.addEventListener("animationend", () => {
        finalCloseAction();
        this.element.classList.remove("animate-out", "fade-out"); // Manually removing modal overlay exit-styles
        dialog.classList.remove(...dialogExitStyles);
      }, {once: true});
      this.element.classList.add("animate-out", "fade-out"); // Manually applying modal overlay exit-styles
      dialog.classList.add(...dialogExitStyles);
    } else {
      finalCloseAction();
    }
  }

  keyup = (event) => {
    if (event.keyCode === 27) {
      this.handleClose();
    }
  };

  handleOpen = (event) => {
    if (event?.detail?.modal === this.element.id) {
      this.element.ariaModal = "true";
      this.dispatch("open", {prefix: "tlw-modal", bubbles: true});
      this.element.classList.remove("hidden");
    }
  };

  addUrlQueryParam = () => {
    const search = new URLSearchParams(window.location.search);
    if (this.queryParamValueValue !== "") {
      search.set(this.queryParamValue, this.queryParamValueValue);
    } else {
      search.delete(this.queryParamValue);
    }
    this.updateUrlQuery(search);
  };

  removeUrlQueryParam() {
    const search = new URLSearchParams(window.location.search);
    search.delete(this.queryParamValue);
    this.updateUrlQuery(search);
  }

  updateUrlQuery = (search: URLSearchParams) => {
    const basePath = [window.location.pathname, search.toString()]
      .filter(Boolean)
      .join("?");

    history.replaceState(null, "", basePath + window.location.hash);
  };

  private isActiveModal(): boolean {
    const openModals = document.querySelectorAll<HTMLElement>("[data-controller='admin--modal--modal']:not(.hidden)");
    if (openModals.length <= 1) return true;

    // Last visible modal in DOM order is the topmost/active
    const topmost = openModals[openModals.length - 1];
    return topmost === this.element;
  }
}
